import type {
  AdminFeedbackQuery,
  AdminFeedbackRow,
  MyFeedbackRow,
  PaginatedResult,
} from '@ai-meeting/shared'
import { apiClient, toApiError } from './api'
import { getServiceStrings } from './i18n'

/**
 * Submits app-wide feedback (BRAI-185) for the current user. The backend
 * derives the email from the authenticated JWT, so only the comment is sent.
 * `meetingId` is an optional hint (BRAI-175) so the backend can snapshot the
 * meeting title; the backend re-verifies ownership, it never trusts a
 * client-supplied title.
 */
export async function submitFeedback(comment: string, meetingId?: string): Promise<void> {
  try {
    await apiClient.post('/api/feedback', { comment, meetingId })
  } catch (error) {
    throw toApiError(error, getServiceStrings().feedbackSubmitError)
  }
}

/**
 * Fetches the authenticated user's own feedback history (BRAI-175), newest
 * first, for the "My Feedback" list in the Quick Feedback popover.
 */
export async function fetchMyFeedback(): Promise<MyFeedbackRow[]> {
  try {
    const { data } = await apiClient.get<MyFeedbackRow[]>('/api/feedback')
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().feedbackSubmitError)
  }
}

/**
 * Fetches the paginated app-wide feedback list for the admin management
 * screen. Backend enforces the admin role.
 */
export async function fetchAdminFeedback(
  query: AdminFeedbackQuery
): Promise<PaginatedResult<AdminFeedbackRow>> {
  try {
    const { data } = await apiClient.get<PaginatedResult<AdminFeedbackRow>>('/api/admin/feedback', {
      params: query,
    })
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().adminLoadError)
  }
}
